import React from 'react';

import { ENV_API_PATH } from '@cd3p/core/constants/common';
import { Typography } from 'components';
import i18n from 'i18n';

export type IntegrationSetting = {
  name: string;
  label: string;
  inputType?: string;
  defaultValue?: string;
  isRequired?: boolean;
  placeholder?: string;
};

export type GpsProviderConfig = {
  name: string;
  label: string;
  instructions?: JSX.Element | string;
  settings: IntegrationSetting[];
};

const whitelisted = [
  '52.2.227.232',
  '35.171.94.153',
  '54.145.30.198',
  '18.233.30.225',
  '34.206.241.37',
];

const WhiteListedSection = () => (
  <>
    <Typography variant="body1" marginBottom="1rem" fontWeight="bold">
      {i18n.t('settings.integrationTab.whiteListedIpsLabel') as string}
    </Typography>
    <ul>
      {whitelisted.map(it => (
        <li key={it}>{it}</li>
      ))}
    </ul>
  </>
);

export const gpsIntegrations: GpsProviderConfig[] = [
  {
    name: 'TrackIt',
    label: 'TrackIt',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.trackit.gps.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'TrackItWebKey',
        label: i18n.t('settings.integrations.trackIt.trackItWebKeyLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.trackIt.trackItWebKeyPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'TrackItPullingRateLimit',
        label: i18n.t(
          'settings.integrations.trackIt.trackItPullingRateLimitLabel',
        ),
        inputType: 'number',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.trackIt.trackItPullingRateLimitPlaceholder',
        ),
        isRequired: false,
      },
    ],
  },
  {
    name: 'DigitalFleet',
    label: 'Digital Fleet',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {
          i18n.t(
            'settings.integrations.digitalFleet.gps.instructions',
          ) as string
        }
      </Typography>
    ),
    settings: [
      {
        name: 'DigitalFleetApiKey',
        label: i18n.t('settings.integrations.digitalFleet.apiKeyLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.digitalFleet.apiKeyPlaceholder',
        ),
        isRequired: true,
      },
    ],
  },
  {
    name: 'Hscc',
    label: 'HSCC',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.hscc.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'HsccClientId',
        label: i18n.t('settings.integrations.hscc.clientIdLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.hscc.clientIdPlaceholder'),
        isRequired: true,
      },
      {
        name: 'HsccClientSecret',
        label: i18n.t('settings.integrations.hscc.clientSecretLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.hscc.clientSecretPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'HsccGrandType',
        label: i18n.t('settings.integrations.hscc.grandTypeLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.hscc.grandTypePlaceholder'),
        isRequired: true,
      },
      {
        name: 'HsccScope',
        label: i18n.t('settings.integrations.hscc.scopeLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.hscc.scopePlaceholder'),
        isRequired: true,
      },
    ],
  },
  {
    name: 'GeoTab',
    label: 'GeoTab',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integration.geotab.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'GeoTabLogin',
        label: i18n.t('settings.integrations.geoTab.loginLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.geoTab.loginPlaceholder'),
        isRequired: true,
      },
      {
        name: 'GeoTabPassword',
        label: i18n.t('settings.integrations.geoTab.passwordLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.geoTab.passwordPlaceholder'),
        isRequired: true,
      },
      {
        name: 'GeoTabDb',
        label: i18n.t('settings.integrations.geoTab.databaseLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.geoTab.databasePlaceholder'),
        isRequired: true,
      },
    ],
  },
  {
    name: 'VerizonConnect',
    label: 'Verizon Connect',
    instructions: (
      <>
        <Typography variant="body1" marginBottom="1rem">
          {
            i18n.t(
              'settings.integrations.verizonConnect.instructions',
            ) as string
          }
        </Typography>

        <a href="https://reveal-help.verizonconnect.com/hc/en-us/articles/5491815998099-Create-API-and-webhook-integrations#UUID-b41f4184-999d-f93e-5706-9e2dac6a368e">
          {
            i18n.t(
              'settings.integrations.verizonConnect.instructions.link',
            ) as string
          }
        </a>
      </>
    ),
    settings: [
      {
        name: 'VerizonUserName',
        label: i18n.t('settings.integrations.verizonConnect.userNameLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.verizonConnect.userNamePlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'VerizonPassword',
        label: i18n.t('settings.integrations.verizonConnect.passwordLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.verizonConnect.passwordPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'VerizonAppId',
        label: i18n.t('settings.integrations.verizonConnect.appIdLabel'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.verizonConnect.appIdPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'VerizonUrl',
        label: i18n.t('settings.integrations.verizonConnect.urlLabel'),
        inputType: 'url',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.verizonConnect.urlPlaceholder',
        ),
        isRequired: true,
      },
    ],
  },
  {
    name: 'KeyStoneGps',
    label: 'KeyStone GPS',
    instructions: (
      <>
        <Typography variant="body1" marginBottom="1rem">
          {i18n.t('settings.integrations.keyStoneGPS.instructions') as string}
        </Typography>
        <WhiteListedSection />
      </>
    ),
    settings: [
      {
        name: 'KeyStoneGpsBaseUrl',
        label: i18n.t('settings.integrations.keyStoneGps.baseUrlLabel'),
        inputType: 'url',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.keyStoneGps.baseUrlPlaceholder',
        ),
        isRequired: true,
      },
    ],
  },
  {
    name: 'Samsara',
    label: 'Samsara',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.samsara.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'SamsaraToken',
        label: i18n.t('settings.integrations.samsara.tokenLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.samsara.tokenPlaceholder'),
        isRequired: true,
      },
    ],
  },
];

export enum TicketIntegrationType {
  Polling = 'polling',
  Webhook = 'webhook',
  Other = 'Other',
}

export type TicketIntegrationConfig = {
  type: TicketIntegrationType;
  name: string;
  label: string;
  instructions?: JSX.Element | string;
  settings?: IntegrationSetting[];
  webhookUrlTemplate?: string;
};

export const ticketIntegrations: TicketIntegrationConfig[] = [
  {
    type: TicketIntegrationType.Polling,
    name: 'TrackIt',
    label: 'TrackIt',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.trackit.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'TrackItApiKey',
        label: i18n.t('settings.integrations.trackIt.trackItApiKeyLabel'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.trackIt.trackItApiKeyPlaceholder',
        ),
        isRequired: true,
      },
    ],
  },
  {
    type: TicketIntegrationType.Polling,
    name: 'Goldgate',
    label: 'Goldgate',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.goldgate.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'GoldgateBaseUrl',
        label: i18n.t('settings.integrations.goldgate.baseUrl'),
        inputType: 'url',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.goldgate.baseUrlPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'GoldgateToken',
        label: i18n.t('settings.integrations.goldgate.token'),
        inputType: 'password',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.goldgate.tokenPlaceholder'),
        isRequired: true,
      },
    ],
  },
  {
    type: TicketIntegrationType.Webhook,
    name: 'MPAQ',
    label: 'MPAQ',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.mpaq.instructions') as string}
      </Typography>
    ),
    webhookUrlTemplate: `${ENV_API_PATH}provider/{providerId}/integration/mpaq`,
  },
  {
    type: TicketIntegrationType.Polling,
    name: 'ErieStrayer',
    label: 'Erie Strayer',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.erieStrayer.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'EriUrl',
        label: i18n.t('settings.integrations.eri.eriUrl'),
        inputType: 'url',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.eri.eriUrlPlaceholder'),
        isRequired: true,
      },
      {
        name: 'EriPassword',
        label: i18n.t('settings.integrations.eri.eriPassword'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.eri.eriPasswordPlaceholder'),
        isRequired: true,
      },
      {
        name: 'EriUserName',
        label: i18n.t('settings.integrations.eri.eriUserName'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t('settings.integrations.eri.eriUserNamePlaceholder'),
        isRequired: true,
      },
    ],
  },
  {
    type: TicketIntegrationType.Polling,
    name: 'Marcotte',
    label: 'Marcotte',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.marcotte.instructions') as string}
      </Typography>
    ),
    settings: [
      {
        name: 'MarcotteBaseUrl',
        label: i18n.t('settings.integrations.marcotte.marcotteUrl'),
        inputType: 'url',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.marcotte.marcotteUrlPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'MarcotteUserName',
        label: i18n.t('settings.integrations.marcotte.marcotteUserName'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.marcotte.marcotteUserNamePlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'MarcottePassword',
        label: i18n.t('settings.integrations.marcotte.marcottePassword'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.marcotte.marcottePasswordPlaceholder',
        ),
        isRequired: true,
      },
    ],
  },
  {
    type: TicketIntegrationType.Webhook,
    name: 'KeyStone',
    label: 'Keystone',
    webhookUrlTemplate: `${ENV_API_PATH}provider/{providerId}/integration/keystone`,
    instructions: (
      <>
        <Typography variant="body1" marginBottom="1rem">
          {
            i18n.t(
              'settings.integrations.keyStoneTicket.instructions',
            ) as string
          }
        </Typography>
        <WhiteListedSection />
      </>
    ),
  },
  {
    type: TicketIntegrationType.Webhook,
    name: 'DigitalFleet',
    label: 'Digital Fleet',
    instructions: (
      <Typography variant="body1" marginBottom="1rem">
        {i18n.t('settings.integrations.digitalFleet.instructions') as string}
      </Typography>
    ),
    webhookUrlTemplate: `${ENV_API_PATH}provider/{providerId}/integration/digitalfleet`,
  },
  {
    type: TicketIntegrationType.Other,
    name: 'ConcretelyPrinter',
    label: 'Concretely Printer',
    instructions: (
      <>
        {i18n.t('settings.integrations.printer.instruction') as string}
        <br />
        <br />
        <a href="https://cdn.concretely.co/printer/Concretely_setup.zip">
          {i18n.t('settings.integrations.printer.downloadPrinter') as string}
        </a>
        <br />
        <a href="https://cdn.concretely.co/printer/installation.pdf">
          {
            i18n.t(
              'settings.integrations.printer.downloadInstructions',
            ) as string
          }
        </a>
      </>
    ),
    settings: [
      {
        name: 'PrinterTruckNumberField',
        label: i18n.t('settings.integrations.printer.truckNumberField'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.printer.truckNumberFieldPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'PrinterPlantIdField',
        label: i18n.t('settings.integrations.printer.plantIdField'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.printer.plantIdFieldPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'PrinterOrderNumberField',
        label: i18n.t('settings.integrations.printer.orderNumberField'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.printer.orderNumberFieldPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'PrinterTicketNumberField',
        label: i18n.t('settings.integrations.printer.ticketNumberField'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.printer.ticketNumberFieldPlaceholder',
        ),
        isRequired: true,
      },
      {
        name: 'PrinterBatchSizeField',
        label: i18n.t('settings.integrations.printer.batchSizeField'),
        inputType: 'text',
        defaultValue: '',
        placeholder: i18n.t(
          'settings.integrations.printer.batchSizeFieldPlaceholder',
        ),
        isRequired: true,
      },
    ],
  },
  {
    type: TicketIntegrationType.Other,
    name: 'ConcretelyConnect',
    label: 'Concretely Connect',
    instructions: (
      <>
        {i18n.t('settings.integrations.connector.description') as string}
        <br />
        <br />
        <a href="https://cdn.concretely.co/connector/instruction.pdf">
          {
            i18n.t(
              'settings.integrations.connector.downloadInstructions',
            ) as string
          }
        </a>
      </>
    ),
    settings: [],
  },
];
